import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";

import { LineApi } from "@api";
import {
    ComponentHeaderAuth,
    ComponentSocialMedia,
    StyledContainerAuthSignUp,
    StyledContainerSignIn,
} from "@components";
import {
    FACEBOOK_INFO_URL,
    LINE_AUTH_URL,
    PATH_HOME,
    PATH_SIGN_UP,
    PATH_SIGN_UP_FORM,
    PATH_WELCOME,
    enumExternalAuthType,
    enumExternalMethod,
} from "@configs";
import { IExternalAuthState, IFBLogin, IFBSignup, ILineLogin, ILineSignup } from "@interfaces";
import liff from "@line/liff";
import { ModuleLine, ModuleLineTelSignUp, ModuleTelSignUp } from "@modules";
import {
    addConfigClient,
    fbLogin,
    fbSignup,
    lineLogin,
    logout,
    resetAuthState,
    selectAuth,
    selectConfigClient,
    selectMerchant,
    setConfigClient,
    setExternalLogin,
    setFromLogin,
    setLineToken,
} from "@redux";
import CacheStore from "@Rocket-Innovation/components/store/CacheStore";
import { formatMessage, useNotify } from "@utils";
import { Skeleton } from "antd";
import axios from "axios";
const varAccessToken = "access_token";

export const PageSignIn = () => {
    //page hook
    const { t } = useTranslation();
    const history = useHistory();
    const { error, success, warning } = useNotify();
    const dispatch = useDispatch();
    const { hash } = useLocation();
    const configClient = useSelector(selectConfigClient);
    const { _id } = useSelector(selectMerchant);
    const [checkSuccess, setCheckSuccess] = useState<boolean>(false);
    const isReady = CacheStore((state: any) => state.isReady);

    //redux states
    const merchant = useSelector(selectMerchant);
    const { themes } = useSelector(selectMerchant);
    const { isSignUp, externalLogin, line, auth } = useSelector(selectAuth);
    //page variable
    const state: IExternalAuthState = {
        type: enumExternalAuthType.SIGNUP,
    };
    const signUpMethod = merchant?.signUpSettings?.signUpMethod;
    const [isLoading, setIsloading] = useState(true);

    useEffect(() => {
        setIsloading(true);
        if (merchant) {
            setIsloading(false);
        }
    });
    useEffect(() => {
        useCache.setState({ isReady: false });
        const unloadHandler = (event: Event) => {
            dispatch(resetAuthState());
            localStorage.clear();
            sessionStorage.clear();
        };
        if (
            externalLogin === enumExternalMethod.LINEOA_TEL ||
            externalLogin === enumExternalMethod.LINE
        ) {
            window.addEventListener("unload", unloadHandler);
            if (liff.isInClient()) {
                liff._addListener("unload", unloadHandler);
            }
        }

        return () => {
            if (
                externalLogin === enumExternalMethod.LINEOA_TEL ||
                externalLogin === enumExternalMethod.LINE
            ) {
                window.removeEventListener("unload", unloadHandler);
                if (liff.isInClient()) {
                    liff._removeListener("unload", unloadHandler);
                }
            }
        };
    }, []);

    useEffect(() => {
        if (hash && hash.includes(varAccessToken)) {
            const hashObj = hash.split("&").reduce((res: any, item) => {
                const parts = item.split("=");
                res[parts[0]] = parts[1];
                return res;
            }, {});
            const accessToken = hashObj?.access_token || undefined;
            if (accessToken) {
                callbackGetFbInfo(accessToken);
            }
        }
        if (
            externalLogin === enumExternalMethod.LINE ||
            externalLogin === enumExternalMethod.LINEOA_TEL
        ) {
            if (sessionStorage.getItem("DISABLE_APP_ROUTE") !== "true") {
                history.push(PATH_WELCOME);
            }
        }
    }, []);

    const callbackGetFbInfo = async (accessToken: string) => {
        const url = `${FACEBOOK_INFO_URL}&access_token=${accessToken}`;
        try {
            const res = await axios.get(url);
            if (res.data) {
                let response;
                if (!isSignUp) {
                    const fbLoginData: IFBLogin = {
                        userId: res.data?.id,
                        token: accessToken,
                    };

                    response = await dispatch(fbLogin(fbLoginData));
                    success(t("page.login_successfully"));
                    //WHAT: wrap function in create async thunk
                    //@ts-ignore
                    unwrapResult(response);
                } else {
                    const fbSignupData: IFBSignup = {
                        userId: res.data?.id,
                        token: accessToken,
                    };
                    response = await dispatch(fbSignup(fbSignupData));
                    //WHAT: wrap function in create async thunk
                    //@ts-ignore
                    unwrapResult(response);
                }
                //@ts-ignore
                if (!response?.payload.accessToken) {
                    error(t("page.email_password_correct"));
                } else {
                    //@ts-ignore
                    const authData = response.payload;
                    const index = configClient.findIndex((item) => item.merchantId === _id);
                    if (index >= 1) {
                        dispatch(
                            setConfigClient({
                                index,
                                configClient: {
                                    merchantId: _id || "",
                                    token: authData,
                                },
                            })
                        );
                    } else {
                        dispatch(
                            addConfigClient({
                                merchantId: _id || "",
                                token: authData,
                            })
                        );
                    }
                    !isSignUp ? history.push(PATH_HOME) : history.push(PATH_SIGN_UP_FORM);
                }
            } else {
                // dispatch(logout());
                error(t("message.sign_failed"));
            }
        } catch (err: any) {
            if (err.response) {
                error(formatMessage(err.response));
            } else error(t("message.sign_failed"));
            // history.push(PATH_SIGN_UP);
        }
    };

    const errorHandler = (errors: any) => {
        //handle Error
        if (errors.response) {
            const { data, status } = errors.response;
            if (data?.message && status !== 500) {
                error(data?.message);
            } else {
                error(t("page.sign_in_failed"));
            }
            dispatch(logout());
        }
    };

    useEffect(() => {
        let isLoading = true;
        if (
            (externalLogin === enumExternalMethod.LINE ||
                externalLogin === enumExternalMethod.LINEOA_TEL) &&
            !auth?.accessToken
        ) {
            externalLine();
        }

        return () => {
            isLoading = false;
        };
    }, []);

    const externalLine = async () => {
        await null;
        // alert(`${line?.code}, ${localStorage.getItem("LINE_LOGIN_PROCESS")}`);
        const lineLoginApi = LineApi(LINE_AUTH_URL);

        let getAccToken: any = "";
        let getTokenId: any = "";
        try {
            if (line?.code && localStorage.getItem("LINE_LOGIN_PROCESS") === "true") {
                // alert("init on sign in page");
                // Focus here!
                if (isReady) {
                    getAccToken = await liff.getAccessToken();
                    getTokenId = await liff.getIDToken();
                    // const lineState = JSON.parse(line?.state) as ILineState;
                    const lineState = { type: line.state };
                    if (lineState.type === enumExternalAuthType.LOGIN) {
                        const lineLoginData: ILineLogin = {
                            token: getAccToken,
                            refreshToken: "123",
                            tokenId: getTokenId,
                        };
                        // if line + tel go to signup tele
                        if (signUpMethod === enumExternalMethod.LINEOA_TEL) {
                            dispatch(setLineToken(lineLoginData));
                            setCheckSuccess(true);
                            history.push(PATH_SIGN_UP);
                            return;
                        }
                        const res = await dispatch(lineLogin(lineLoginData));
                        //WHAT: wrap function in create async thunk
                        //@ts-ignore
                        unwrapResult(res);
                        //@ts-ignore
                        if (!res.payload.accessToken) {
                            error(t("page.email_password_correct"));
                        } else {
                            //@ts-ignore
                            const authData = res.payload;
                            const index = configClient.findIndex((item) => item.merchantId === _id);
                            if (index >= 1) {
                                dispatch(
                                    setConfigClient({
                                        index,
                                        configClient: {
                                            merchantId: _id || "",
                                            token: authData,
                                        },
                                    })
                                );
                            } else {
                                dispatch(
                                    addConfigClient({
                                        merchantId: _id || "",
                                        token: authData,
                                    })
                                );
                            }
                            setCheckSuccess(true);
                            success(t("page.login_successfully"));
                            history.push(PATH_HOME);
                            dispatch(setFromLogin(true));
                        }
                    } else if (lineState.type === enumExternalAuthType.SIGNUP) {
                        if (signUpMethod === enumExternalMethod.LINE) {
                            dispatch(
                                setExternalLogin({
                                    type: enumExternalMethod.LINE,
                                    isSignUp: true,
                                })
                            );
                        } else {
                            dispatch(
                                setExternalLogin({
                                    type: enumExternalMethod.LINEOA_TEL,
                                    isSignUp: true,
                                })
                            );
                        }

                        const lineSignupData: ILineSignup = {
                            token: getAccToken,
                            refreshToken: "123",
                            tokenId: getTokenId,
                            inviteId: "",
                        };

                        // if line + tel go to signup tele
                        if (signUpMethod === enumExternalMethod.LINEOA_TEL) {
                            dispatch(setLineToken(lineSignupData));
                            setCheckSuccess(true);
                            history.push(PATH_SIGN_UP);
                            return;
                        }

                        const res = await dispatch(lineLogin(lineSignupData));

                        //WHAT: wrap function in create async thunk
                        //@ts-ignore
                        unwrapResult(res);
                        //@ts-ignore
                        if (!res.payload.accessToken) {
                            error(t("page.email_password_correct"));
                        } else {
                            //@ts-ignore
                            const authData = res.payload;
                            const index = configClient.findIndex((item) => item.merchantId === _id);
                            if (index >= 1) {
                                dispatch(
                                    setConfigClient({
                                        index,
                                        configClient: {
                                            merchantId: _id || "",
                                            token: authData,
                                        },
                                    })
                                );
                            } else {
                                dispatch(
                                    addConfigClient({
                                        merchantId: _id || "",
                                        token: authData,
                                    })
                                );
                            }
                            setCheckSuccess(true);
                            success(t("page.login_successfully"));
                            localStorage.removeItem("LINE_LOGIN_PROCESS");
                            sessionStorage.removeItem("DISABLE_APP_ROUTE");
                            authData.isFirstLogin
                                ? history.push(PATH_SIGN_UP_FORM)
                                : history.push(PATH_HOME);
                        }
                    } else {
                        dispatch(logout());
                    }
                }
            } else {
                dispatch(logout());
            }
        } catch (errors) {
            errorHandler(errors);
            console.log("err", errors);
            dispatch(
                setExternalLogin({
                    type: enumExternalMethod.LINE,
                    isSignUp: false,
                })
            );
        }
    };

    return (
        <StyledContainerSignIn color={themes?.themeColor!}>
            <ComponentHeaderAuth>
                <>
                    <StyledContainerAuthSignUp bg_color={themes?.themeColor!}>
                        <div className="title_auth text-sub-header">
                            <Skeleton
                                loading={isLoading}
                                active
                                title={false}
                                paragraph={{ rows: 1, width: 132 }}
                            >
                                {t("page.sign_up_to")}
                            </Skeleton>
                        </div>
                        {merchant?.advanceSettings?.advanceFeature?.isCorporateUi === true ? (
                            <div className="logo-image">
                                <Skeleton
                                    loading={isLoading}
                                    avatar
                                    active
                                    title={false}
                                    paragraph={false}
                                >
                                    <img src={merchant?.themes?.logoSmallUrl} />
                                </Skeleton>
                            </div>
                        ) : (
                            <div className="logo-image">
                                <Skeleton
                                    loading={isLoading}
                                    avatar
                                    active
                                    title={false}
                                    paragraph={false}
                                >
                                    <img src={merchant?.themes?.logoSmallUrl} />
                                </Skeleton>
                            </div>
                        )}

                        <div className="title_auth text-sub-header">
                            {/* <Skeleton
                                loading={isLoading}
                                active
                                title={false}
                                paragraph={{ rows: 1, width: 132 }}
                            >
                                {merchant?.name ? merchant?.name : ""}
                            </Skeleton> */}
                        </div>
                        {externalLogin === enumExternalMethod.LINEOA_TEL ? (
                            <>
                                <ModuleLineTelSignUp />
                            </>
                        ) : (
                            <>
                                {signUpMethod === enumExternalMethod.LINE && (
                                    <>
                                        <ComponentSocialMedia
                                            socials={[<ModuleLine state={state} />]}
                                        />
                                    </>
                                )}
                                {signUpMethod === enumExternalMethod.LINEOA_TEL && (
                                    <>
                                        <ComponentSocialMedia
                                            socials={[<ModuleLine state={state} />]}
                                        />
                                    </>
                                )}
                                {signUpMethod === enumExternalMethod.TEL && <ModuleTelSignUp />}
                            </>
                        )}
                    </StyledContainerAuthSignUp>
                </>
            </ComponentHeaderAuth>
        </StyledContainerSignIn>
    );
};
